<template lang="pug">
.groups
  header.groups__hero
    .groups__hero-inner
      p.groups__eyebrow
        span.groups__eyebrow-pip
        | Fase de grupos
      h1.groups__title Grupos e Classificação
      p.groups__sub O sistema sorteia os grupos pela quantidade de inscritos, roda o todos-contra-todos e os classificados avançam para a chave.
      .groups__event-badge(v-if="getCurrentEvent")
        span.groups__event-label Evento
        span.groups__event-name {{ getCurrentEvent.name }}

  .groups__wrap
    .groups__controls
      Select.select-category(
        defaultText="Selecionar Categoria"
        :options="takeCategories"
        :value="categorySelected"
        type='drop'
        @emit="doSetCategory"
      )
      label.groups__toggle(v-if="canManage && categorySelected")
        input(type="checkbox" :checked="enabled" @change="doToggle")
        span.groups__toggle-track
          span.groups__toggle-knob
        span.groups__toggle-txt Usar fase de grupos
      button.groups__btn(
        v-if="canManage && categorySelected && !editing && !hasGroups"
        @click="doGenerate"
      ) Sortear grupos
      button.groups__btn.groups__btn--regen(
        v-if="canManage && categorySelected && !editing && hasGroups && !anyStarted"
        @click="doRegenerate"
      ) Refazer sorteio
      button.groups__btn.groups__btn--manual(
        v-if="canManage && categorySelected && !editing && !anyStarted"
        @click="doStartEdit"
      ) {{ hasGroups ? 'Ajustar grupos manualmente' : 'Montar grupos manualmente' }}
      button.groups__btn.groups__btn--live(
        v-if="canManage && !editing"
        @click="$router.push('/Roleta')"
      ) 🎡 Sorteio na roleta (live)
      button.groups__btn.groups__btn--bracket(
        v-if="canManage && categorySelected && hasGroups && allClosed"
        @click="doGenerateBracket"
      ) Gerar chave dos classificados

    //- Sem este aviso, os botões de edição simplesmente sumiam depois da 1ª luta.
    p.groups__feedback(v-if="canManage && categorySelected && hasGroups && anyStarted && !editing")
      | Grupos travados para edição: esta categoria já tem luta realizada.
    p.groups__feedback(v-if="feedback") {{ feedback }}

    //- ===== Composição manual (Admin/Organizador) =====
    .gedit(v-if="editing")
      .gedit__head
        h3.gedit__title Composição manual dos grupos
        label.gedit__count
          span Nº de grupos
          input(type="number" min="1" max="12" v-model.number="editGroupCount")
      p.gedit__hint Escolha o grupo de cada robô. Quem ficar em "Fora" não entra na fase de grupos. Cada grupo precisa de pelo menos 2 robôs.
      .gedit__counts
        span.gedit__chip(v-for="(c, i) in editCounts" :key="'c' + i" :class="{ 'is-low': c < 2 }") Grupo {{ letterOf(i) }}: {{ c }}
        span.gedit__chip.gedit__chip--out Fora: {{ outCount }}
      .gedit__empty(v-if="!eligibleRobots.length") Nenhum robô com inspeção aprovada nesta categoria.
      .gedit__list(v-else)
        .gedit__row(v-for="r in eligibleRobots" :key="r.id")
          span.gedit__robot {{ r.name }}
          select.gedit__select(v-model.number="assign[r.id]")
            option(:value="-1") Fora
            option(v-for="(c, i) in editCounts" :key="'o' + i" :value="i") Grupo {{ letterOf(i) }}
      p.gedit__err(v-if="editError") {{ editError }}
      .gedit__foot
        button.groups__btn(@click="doSaveComposition") Salvar composição
        button.gedit__cancel(@click="editing = false") Cancelar

    .groups__empty(v-if="!categorySelected")
      span.groups__empty-ic 🗂️
      h3.groups__empty-title Selecione uma categoria
      p.groups__empty-text Escolha uma categoria acima para ver os grupos e a classificação.

    .groups__empty(v-else-if="!hasGroups")
      span.groups__empty-ic 🎲
      h3.groups__empty-title Sem fase de grupos gerada
      p.groups__empty-text(v-if="canManage") Com os robôs aprovados na inspeção, use "Sortear grupos" para o sorteio automático ou "Montar grupos manualmente" para escolher a composição.
      p.groups__empty-text(v-else) Esta categoria ainda não teve os grupos sorteados.

    .groups__grid(v-else)
      .gcard(v-for="g in standings" :key="g.group_id")
        .gcard__head
          h2.gcard__name Grupo {{ g.group_name }}
          span.gcard__status(:class="g.closed ? 'is-closed' : 'is-open'") {{ g.closed ? 'Encerrado' : 'Em andamento' }}
        table.gtable
          thead
            tr
              th.gtable__pos #
              th.gtable__robot Robô
              th PJ
              th V
              th D
              th(title="Vitórias por nocaute/desistência") KO
              th.gtable__pts Pts
          tbody
            tr(v-for="(s, i) in g.standings" :key="s.robot_id" :class="{ 'is-qualifier': isQualified(s.robot_id) }")
              td.gtable__pos {{ i + 1 }}
              td.gtable__robot
                span {{ s.robot_name }}
                span.gtable__badge(v-if="isQualified(s.robot_id)") {{ i < 2 ? 'classificado' : 'melhor 3º' }}
              td {{ s.played }}
              td {{ s.wins }}
              td {{ s.losses }}
              td {{ s.kos }}
              td.gtable__pts {{ s.points }}
        p.gcard__note(v-if="!g.closed")
          | Classificação parcial: os classificados aparecem quando todas as lutas deste grupo terminarem.
        p.gcard__note(v-else-if="!allClosed")
          | Grupo encerrado. Os melhores 3ºs só são definidos quando todos os grupos terminarem.
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { Select } from '@/components/atoms'

export default {
  name: 'Groups',
  data () {
    return {
      categorySelected: '',
      enabled: false,
      standings: [],
      feedback: '',
      // composição manual
      editing: false,
      editGroupCount: 4,
      eligibleRobots: [],
      assign: {}, // robot_id -> índice do grupo (-1 = fora)
      editError: ''
    }
  },
  created () {
    this.setCategories()
    this.setEvents().then(() => {
      if (this.getCurrentEventId) this.setEventSummary(this.getCurrentEventId)
    })
  },
  components: { Select },
  computed: {
    ...mapGetters('categories', ['getCategories']),
    ...mapGetters('events', ['getCurrentEventId', 'getCurrentEvent', 'getCurrentEventArenaId', 'getEventSummary']),
    // Só as categorias DO EVENTO (o resumo traz uma linha por categoria cadastrada
    // nele). Antes listava todas as do sistema — aparecia Fairyweight num evento
    // criado só com Ant e Beetle. Fallback: todas, se o resumo ainda não carregou.
    takeCategories () {
      const doEvento = (this.getEventSummary || [])
        .filter(c => c.category_id)
        .map(c => ({ text: c.category_name, value: c.category_id }))
      if (doEvento.length) return doEvento
      return [...this.getCategories].map(c => ({ text: c.name, value: c.id }))
    },
    canManage () {
      const roles = (this.$store.getters['users/getUser'] || {}).roles
      return Array.isArray(roles) && (roles.includes(1) || roles.includes(6))
    },
    hasGroups () {
      return this.standings.length > 0
    },
    allClosed () {
      return this.hasGroups && this.standings.every(g => g.closed)
    },
    anyStarted () {
      // alguma luta de grupo já disputada (algum robô com jogo computado)
      return this.standings.some(g => g.standings.some(s => s.played > 0))
    },
    // Quem está classificado. Só marca quando a disputa REALMENTE definiu:
    //  - top 2: quando aquele grupo terminou (antes disso a ordem é provisória);
    //  - melhores 3ºs: só quando TODOS os grupos terminaram, porque dependem de
    //    comparar as campanhas entre grupos (mesma regra do backend).
    qualifiedIds () {
      const ids = {}
      const grupos = this.standings || []
      grupos.forEach(g => {
        if (!g.closed) return
        ;(g.standings || []).slice(0, 2).forEach(s => { ids[s.robot_id] = true })
      })
      if (this.allClosed && grupos.length) {
        const base = 2 * grupos.length
        let chave = 1
        while (chave < base) chave *= 2
        const vagas = chave - base
        if (vagas > 0) {
          const terceiros = grupos.map(g => (g.standings || [])[2]).filter(Boolean)
          terceiros.sort((a, b) =>
            (b.points - a.points) ||
            (b.kos - a.kos) ||
            (b.score_sum - a.score_sum) ||
            (a.robot_id - b.robot_id))
          terceiros.slice(0, vagas).forEach(s => { ids[s.robot_id] = true })
        }
      }
      return ids
    },
    // quantos robôs há em cada grupo na composição sendo editada
    editCounts () {
      const n = Math.min(12, Math.max(1, this.editGroupCount || 1))
      const arr = new Array(n).fill(0)
      Object.keys(this.assign).forEach(k => {
        const g = this.assign[k]
        if (g >= 0 && g < n) arr[g]++
      })
      return arr
    },
    outCount () {
      const n = this.editCounts.length
      return Object.keys(this.assign).filter(k => {
        const g = this.assign[k]
        return !(g >= 0 && g < n)
      }).length
    }
  },
  methods: {
    ...mapActions('categories', ['setCategories']),
    ...mapActions('events', ['setEvents', 'setEventSummary']),
    ...mapActions('fights', [
      'fetchGroupStage', 'fetchGroupStandings', 'setGroupStageEnabled',
      'setGenerateGroups', 'setGenerateBracketFromGroups', 'setGroupsComposition'
    ]),
    ...mapActions('robots', ['fetchRobotsList']),
    letterOf (i) {
      return String.fromCharCode(65 + i)
    },
    isQualified (robotId) {
      return !!this.qualifiedIds[robotId]
    },
    // Abre a composição manual: carrega os robôs elegíveis da categoria e
    // pré-preenche com os grupos atuais (se já houve sorteio).
    doStartEdit () {
      this.editError = ''
      this.feedback = ''
      this.fetchRobotsList({ event_id: this.getCurrentEventId }).then(list => {
        const cat = this.categorySelected
        this.eligibleRobots = (Array.isArray(list) ? list : [])
          .filter(r => r.category_id === cat && r.static_inspection && r.dynamic_inspection)
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        // posição atual de cada robô (índice do grupo na ordem da classificação)
        const current = {}
        this.standings.forEach((g, gi) => {
          g.standings.forEach(s => { current[s.robot_id] = gi })
        })
        const assign = {}
        this.eligibleRobots.forEach(r => {
          assign[r.id] = current[r.id] != null ? current[r.id] : -1
        })
        this.assign = assign
        this.editGroupCount = this.standings.length ||
          Math.max(2, Math.round(this.eligibleRobots.length / 4)) || 2
        this.editing = true
      })
    },
    doSaveComposition () {
      this.editError = ''
      const n = this.editCounts.length
      const groups = []
      for (let i = 0; i < n; i++) {
        const ids = Object.keys(this.assign)
          .filter(k => this.assign[k] === i)
          .map(k => parseInt(k, 10))
        groups.push({ name: this.letterOf(i), robot_ids: ids })
      }
      const small = groups.findIndex(g => g.robot_ids.length < 2)
      if (small >= 0) {
        this.editError = `O grupo ${this.letterOf(small)} precisa de pelo menos 2 robôs.`
        return
      }
      this.setGroupsComposition({
        event_id: this.getCurrentEventId,
        category_id: this.categorySelected,
        arena_id: this.getCurrentEventArenaId,
        groups
      }).then(res => {
        if (!res || res.success === false) {
          this.editError = (res && res.error) || 'Não foi possível salvar a composição.'
          return
        }
        this.editing = false
        this.feedback = `Grupos definidos manualmente: ${groups.length} grupo(s), ${res.fights} luta(s) criada(s).`
        this.loadStage()
      })
    },
    doSetCategory (e) {
      this.categorySelected = e.value
      this.feedback = ''
      this.editing = false
      this.loadStage()
    },
    loadStage () {
      const ids = { event_id: this.getCurrentEventId, category_id: this.categorySelected }
      this.fetchGroupStage(ids).then(res => { this.enabled = !!(res && res.enabled) })
      return this.fetchGroupStandings(ids).then(res => { this.standings = Array.isArray(res) ? res : [] })
    },
    doToggle (e) {
      const enabled = e.target.checked
      this.setGroupStageEnabled({
        event_id: this.getCurrentEventId, category_id: this.categorySelected, enabled
      }).then(res => { this.enabled = !!(res && res.enabled) })
    },
    doGenerate () {
      this.feedback = ''
      this.setGenerateGroups({
        event_id: this.getCurrentEventId,
        category_id: this.categorySelected,
        arena_id: this.getCurrentEventArenaId
      }).then(res => {
        if (!res || res.success === false) {
          this.feedback = (res && res.error) || 'Não foi possível sortear os grupos. Confira os robôs aprovados e se a categoria já tem luta iniciada.'
          return
        }
        this.loadStage()
      })
    },
    doRegenerate () {
      if (!window.confirm('Isso apaga o sorteio atual e as lutas de grupo e sorteia de novo. Continuar?')) return
      this.doGenerate()
    },
    doGenerateBracket () {
      this.feedback = ''
      this.setGenerateBracketFromGroups({
        event_id: this.getCurrentEventId,
        category_id: this.categorySelected,
        arena_id: this.getCurrentEventArenaId
      }).then(res => {
        if (!res || res.success === false) {
          this.feedback = (res && res.error) || 'Não foi possível gerar a chave (a fase de grupos precisa estar concluída).'
          return
        }
        this.feedback = `Chave gerada com ${res.fights_created} luta(s). Veja em Chaves.`
      })
    }
  },
  watch: {
    getCurrentEventId (newId) {
      this.editing = false
      this.categorySelected = '' // categorias mudam com o evento
      if (newId) this.setEventSummary(newId)
      this.standings = []
    },
    // ao reduzir o nº de grupos, quem estava num grupo que sumiu volta para "Fora"
    editGroupCount (n) {
      const max = Math.min(12, Math.max(1, n || 1))
      const next = { ...this.assign }
      let changed = false
      Object.keys(next).forEach(k => {
        if (next[k] >= max) {
          next[k] = -1
          changed = true
        }
      })
      if (changed) this.assign = next
    }
  }
}
</script>

<style lang="sass" scoped>
  $blue: #287AED
  $red: #DD3434
  $green: #2E9E4F
  $gold: #F9A825
  $ink: #0B0E14
  $muted: #5C6673
  $line: rgba(11, 14, 20, 0.10)
  $paper: #FFFFFF
  $mist: #EEF1F6
  $page: #F2F4F8
  $display: 'Saira Condensed', 'Lato', sans-serif
  $mono: 'Space Mono', 'Lato', monospace

  .groups
    color: $ink
    background: $page
    min-height: calc(100vh - 64px)
    *
      box-sizing: border-box

    &__hero
      position: relative
      overflow: hidden
      padding: 30px 0 40px
      background: radial-gradient(circle at 16% 8%, #15233b 0%, $ink 52%, #05070b 100%)
      color: #fff
    &__hero-inner
      max-width: 1240px
      margin: 0 auto
      padding: 0 24px
    &__eyebrow
      display: inline-flex
      align-items: center
      gap: 10px
      font-family: $mono
      text-transform: uppercase
      letter-spacing: 0.22em
      font-size: 12px
      font-weight: 700
      color: rgba(255, 255, 255, 0.7)
      margin: 0 0 12px
    &__eyebrow-pip
      width: 8px
      height: 8px
      border-radius: 50%
      background: linear-gradient(135deg, #5AA0FF, $red)
    &__title
      font-family: $display
      font-size: 44px
      font-weight: 800
      margin: 0
    &__sub
      max-width: 620px
      margin: 12px 0 0
      font-size: 15px
      line-height: 1.5
      color: rgba(255, 255, 255, 0.72)
    &__event-badge
      display: inline-flex
      align-items: center
      gap: 10px
      margin-top: 18px
      padding: 8px 16px
      border-radius: 999px
      background: rgba(255, 255, 255, 0.07)
      border: 1px solid rgba(255, 255, 255, 0.14)
    &__event-label
      font-family: $mono
      font-size: 10px
      text-transform: uppercase
      letter-spacing: 0.14em
      color: rgba(255, 255, 255, 0.55)
    &__event-name
      font-family: $display
      font-size: 16px
      font-weight: 700

    &__wrap
      max-width: 1240px
      margin: 0 auto
      padding: 28px 24px 64px
    &__controls
      display: flex
      align-items: center
      flex-wrap: wrap
      gap: 16px
      margin-bottom: 20px
      .select-category
        min-width: 260px

    &__toggle
      display: inline-flex
      align-items: center
      gap: 10px
      cursor: pointer
      font-family: $display
      font-weight: 600
      font-size: 15px
      input
        display: none
      &-track
        width: 44px
        height: 26px
        border-radius: 999px
        background: #C3CBD8
        position: relative
        transition: background .18s ease
      &-knob
        position: absolute
        top: 3px
        left: 3px
        width: 20px
        height: 20px
        border-radius: 50%
        background: #fff
        transition: transform .18s ease
      input:checked + &-track
        background: $green
      input:checked + &-track &-knob
        transform: translateX(18px)

    &__btn
      padding: 0 22px
      height: 46px
      background: linear-gradient(140deg, $red, #B0231F)
      color: #fff
      border: none
      border-radius: 10px
      font-family: $display
      font-size: 15px
      font-weight: 700
      cursor: pointer
      white-space: nowrap
      &--regen
        background: linear-gradient(140deg, #F57C00, #E65100)
      &--bracket
        background: linear-gradient(140deg, $blue, #0F4FA8)
      &--manual
        background: linear-gradient(140deg, #5C6673, #3C4450)
      &--live
        background: linear-gradient(140deg, #7B2FF7, #4B1DA8)

    &__feedback
      margin: 0 0 18px
      font-size: 14px
      color: $muted

    &__empty
      display: flex
      flex-direction: column
      align-items: center
      text-align: center
      padding: 72px 24px
      background: $paper
      border: 1px dashed rgba(11, 14, 20, 0.16)
      border-radius: 16px
      &-ic
        font-size: 44px
        margin-bottom: 12px
      &-title
        font-family: $display
        font-size: 22px
        font-weight: 700
        margin: 0 0 6px
      &-text
        font-size: 14px
        color: $muted
        margin: 0

    &__grid
      display: grid
      grid-template-columns: repeat(auto-fill, minmax(320px, 1fr))
      gap: 18px

  // ---------- COMPOSIÇÃO MANUAL ----------
  .gedit
    background: $paper
    border: 1px solid $line
    border-radius: 14px
    padding: 20px
    margin-bottom: 22px
    &__head
      display: flex
      align-items: center
      justify-content: space-between
      flex-wrap: wrap
      gap: 12px
    &__title
      font-family: $display
      font-size: 20px
      font-weight: 800
      margin: 0
    &__count
      display: inline-flex
      align-items: center
      gap: 8px
      font-family: $mono
      font-size: 11px
      text-transform: uppercase
      letter-spacing: 0.08em
      color: $muted
      input
        width: 70px
        padding: 8px 10px
        border: 1px solid $line
        border-radius: 8px
        font-family: $display
        font-size: 16px
        font-weight: 700
        color: $ink
    &__hint
      margin: 10px 0 14px
      font-size: 13px
      color: $muted
    &__counts
      display: flex
      flex-wrap: wrap
      gap: 8px
      margin-bottom: 14px
    &__chip
      font-family: $mono
      font-size: 10px
      text-transform: uppercase
      letter-spacing: 0.06em
      padding: 4px 10px
      border-radius: 999px
      background: rgba(40, 122, 237, 0.10)
      color: #0F4FA8
      &.is-low
        background: rgba(221, 52, 52, 0.10)
        color: #B0231F
      &--out
        background: $mist
        color: $muted
    &__list
      display: grid
      grid-template-columns: repeat(auto-fill, minmax(min(100%, 280px), 1fr))
      gap: 8px
    &__row
      display: flex
      align-items: center
      justify-content: space-between
      gap: 10px
      padding: 8px 12px
      border: 1px solid $line
      border-radius: 10px
    &__robot
      font-family: $display
      font-weight: 600
      font-size: 15px
      overflow: hidden
      text-overflow: ellipsis
      white-space: nowrap
    &__select
      flex-shrink: 0
      padding: 6px 8px
      border: 1px solid $line
      border-radius: 8px
      background: #fff
      font-family: inherit
      font-size: 13px
      color: $ink
    &__empty
      padding: 18px
      text-align: center
      color: $muted
      font-size: 14px
      background: $page
      border-radius: 10px
    &__err
      margin: 14px 0 0
      font-size: 13px
      color: $red
      font-weight: 600
    &__foot
      display: flex
      align-items: center
      gap: 12px
      margin-top: 18px
    &__cancel
      background: none
      border: none
      cursor: pointer
      font-family: $display
      font-weight: 700
      font-size: 15px
      color: $muted
      &:hover
        color: $ink

  .gcard
    background: $paper
    border: 1px solid $line
    border-radius: 14px
    overflow: hidden
    &__head
      display: flex
      align-items: center
      justify-content: space-between
      padding: 14px 16px
      border-bottom: 1px solid $line
    &__name
      font-family: $display
      font-size: 20px
      font-weight: 800
      margin: 0
    &__note
      margin: 0
      padding: 10px 14px 14px
      font-size: 11.5px
      line-height: 1.45
      color: $muted
      border-top: 1px dashed $line

    &__status
      font-family: $mono
      font-size: 10px
      text-transform: uppercase
      letter-spacing: 0.1em
      padding: 4px 10px
      border-radius: 999px
      &.is-open
        background: #FFF8E1
        color: #8D6E00
      &.is-closed
        background: rgba(46, 158, 79, 0.12)
        color: #1F7C3C

  .gtable
    width: 100%
    border-collapse: collapse
    font-size: 14px
    th
      font-family: $mono
      font-size: 10px
      text-transform: uppercase
      letter-spacing: 0.08em
      color: $muted
      font-weight: 700
      text-align: center
      padding: 10px 6px
    td
      text-align: center
      padding: 10px 6px
      border-top: 1px solid $line
    &__pos
      width: 34px
      color: $muted
      font-family: $mono
    &__robot
      text-align: left
      font-family: $display
      font-weight: 600
    &__pts
      font-weight: 800
    &__badge
      margin-left: 8px
      font-family: $mono
      font-size: 9px
      text-transform: uppercase
      letter-spacing: 0.06em
      color: $green
      border: 1px solid rgba(46, 158, 79, 0.4)
      border-radius: 999px
      padding: 1px 6px
    tr.is-qualifier td
      background: rgba(46, 158, 79, 0.05)
</style>
